<template>
  <!--
    ArtifactDetail SECTION — Metric artifact, "Audit" tab.

    WHAT: The metric's traffic-light audit — each stage judges reality (pulled
      through the production read paths by a claude-code-worker job) against the
      metric's stored EXPECTATION. Stage 2 (Aggregate) is live; entity /
      worklist / insight stages land as further worker jobs.

    CONTRACT (props { record }):
      GET  api/impact_analytics/composable-metrics/{id}/audit/
           -> { stages, verdicts: {stage: {...}}, jobs: {stage: {...}} }
      POST api/impact_analytics/composable-metrics/{id}/run-audit/ { stage }
           -> { job_id, status }
      GET  /api/personal-analyst/jobs/{id}/  (poll — lazy-syncs the result)
      Emits nothing — verdicts persist in Snowflake, not on the record.

    NOTES: selfChrome: true — draws its own cards.
  -->
  <div class="space-y-3">
    <div class="flex items-start gap-3">
      <div class="flex-1 min-w-0">
        <div class="text-sm font-semibold text-gray-800">Audit</div>
        <div class="text-xs text-gray-400 mt-0.5">
          Reality vs this metric's expectation, stage by stage. The same checks the weekly
          audit runs — here, on demand.
        </div>
      </div>
      <button
        class="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
        :disabled="recomputing"
        title="Re-run precompute so the checks judge fresh data"
        @click="recompute"
      >
        <font-awesome-icon :icon="['fas', recomputing ? 'circle-notch' : 'arrows-rotate']" :class="recomputing ? 'fa-spin' : ''" class="text-[10px]" />
        {{ recomputing ? 'Recomputing…' : 'Recompute metric' }}
      </button>
    </div>
    <div v-if="recomputeMsg" class="text-xs rounded-lg px-3 py-2 border"
         :class="recomputeErr ? 'text-red-600 bg-red-50 border-red-200' : 'text-gray-600 bg-gray-50 border-gray-200'">
      {{ recomputeMsg }}
    </div>

    <div v-if="loading" class="text-[11px] text-gray-500 px-1 py-6">Loading…</div>
    <div v-else-if="error" class="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
      {{ error }}
    </div>

    <template v-else>
      <div
        v-for="stage in allStages"
        :key="stage.key"
        class="bg-white border border-gray-200 rounded-xl"
      >
        <!-- stage header row -->
        <div class="flex items-center gap-3 px-5 py-3.5">
          <span
            class="w-3 h-3 rounded-full flex-shrink-0"
            :style="{ background: lightColor(stage.key) }"
          ></span>
          <div class="min-w-0 flex-1">
            <div class="text-[13px] font-semibold text-gray-800">{{ stage.label }}</div>
            <div class="text-[11px] text-gray-400">
              <template v-if="!stage.built">Not built yet.</template>
              <template v-else-if="verdictFor(stage.key)">
                {{ verdictFor(stage.key).verdict }} · {{ ago(verdictFor(stage.key).created_at) }}
                <span v-if="fingerprintStale(stage.key)" class="text-amber-600">
                  · contract changed since this verdict</span>
              </template>
              <template v-else>Never run.</template>
            </div>
          </div>
          <button
            v-if="stage.built"
            class="inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-lg px-3 py-1.5 hover:opacity-90 disabled:opacity-50 whitespace-nowrap flex-shrink-0"
            :style="{ background: '#2563EB' }"
            :disabled="running[stage.key]"
            @click="run(stage.key)"
          >
            <font-awesome-icon
              :icon="['fas', running[stage.key] ? 'circle-notch' : 'play']"
              :class="running[stage.key] ? 'fa-spin' : ''"
              class="text-[10px]"
            />
            {{ running[stage.key] ? 'Running…' : 'Run check' }}
          </button>
        </div>

        <!-- verdict detail -->
        <div v-if="stage.built && verdictFor(stage.key)" class="border-t border-gray-100">
          <div v-if="runError[stage.key]" class="px-5 py-2 text-xs text-red-600 bg-red-50">
            {{ runError[stage.key] }}
          </div>

          <!-- reasons -->
          <div v-if="(verdictFor(stage.key).reasons || []).length" class="px-5 py-3.5 border-b border-gray-100">
            <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Findings</div>
            <ul class="space-y-1">
              <li
                v-for="(r, i) in verdictFor(stage.key).reasons"
                :key="i"
                class="text-xs leading-relaxed text-gray-700 flex gap-2"
              >
                <span class="select-none flex-shrink-0" :style="{ color: lightColor(stage.key) }">•</span>
                <span>{{ r }}</span>
              </li>
            </ul>
          </div>

          <!-- diagnosis (non-green only) -->
          <div
            v-if="diagnosisFor(stage.key)"
            class="px-5 py-3.5 border-b border-gray-100 bg-amber-50/40"
          >
            <div class="text-[11px] font-semibold text-amber-700 uppercase tracking-wide mb-1.5">Diagnosis</div>
            <p class="text-xs leading-relaxed text-amber-900">{{ diagnosisFor(stage.key) }}</p>
          </div>

          <!-- invariants -->
          <div v-if="(verdictFor(stage.key).invariants || []).length" class="px-5 py-3.5">
            <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
              Coded Invariants
            </div>
            <div class="space-y-1">
              <div
                v-for="inv in verdictFor(stage.key).invariants"
                :key="inv.name"
                class="flex items-baseline gap-2 text-xs"
              >
                <span
                  class="font-mono text-[10px] font-bold w-9 flex-shrink-0"
                  :style="{ color: invColor(inv.status) }"
                >{{ inv.status }}</span>
                <span class="font-mono text-[11px] text-gray-500 w-52 flex-shrink-0 truncate">{{ inv.name }}</span>
                <span class="text-gray-700 leading-relaxed">{{ inv.detail }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="stage.built && runError[stage.key]" class="border-t border-gray-100 px-5 py-2 text-xs text-red-600 bg-red-50">
          {{ runError[stage.key] }}
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { getJob } from '@/services/api/personalAnalyst';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const POLL_MS = 5000;
const MAX_POLLS = 120;

const STAGES = [
  { key: 'aggregate_check', label: 'Aggregate', built: true },
  { key: 'entity_check', label: 'Entities', built: true },
  { key: 'worklist_check', label: 'Worklists', built: true },
  { key: 'insight_check', label: 'Insights', built: true },
];

const VERDICT_COLORS = { GREEN: '#10B981', AMBER: '#F59E0B', RED: '#EF4444' };

export default {
  name: 'AuditSection',
  components: { FontAwesomeIcon },
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, default: () => ({}) },
    context: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      loading: true,
      error: null,
      verdicts: {},
      jobs: {},
      running: {},   // stage -> bool
      runError: {},  // stage -> message
      pollCounts: {},
      recomputing: false,
      recomputeMsg: '',
      recomputeErr: false,
      allStages: STAGES,
    };
  },
  computed: {
    auditUrl() {
      return `api/impact_analytics/composable-metrics/${this.record.id}/audit/`;
    },
    runUrl() {
      return `api/impact_analytics/composable-metrics/${this.record.id}/run-audit/`;
    },
    contractFingerprint() {
      return ((this.record.composable_config || {}).contract_meta || {}).source_fingerprint || null;
    },
  },
  mounted() { this.load(); },
  beforeUnmount() { Object.values(this._timers || {}).forEach(clearTimeout); },
  methods: {
    async recompute() {
      // Manual precompute — the checks judge whatever the tables hold, so
      // this is the honest first step after a build or a definition change.
      this.recomputing = true;
      this.recomputeErr = false;
      this.recomputeMsg = 'Precomputing — a few minutes for a full history…';
      try {
        const { data } = await axios.post(
          `api/impact_analytics/composable-metrics/${this.record.id}/recompute/`,
          {}, { timeout: 600000 },
        );
        this.recomputeErr = false;
        this.recomputeMsg = `Precomputed — ${data.rows_computed ?? '?'} rows. Run the checks below.`;
      } catch (e) {
        this.recomputeErr = true;
        this.recomputeMsg = `Precompute failed: ${e?.response?.data?.error || e.message}`;
      } finally {
        this.recomputing = false;
      }
    },
    verdictFor(stage) { return this.verdicts[stage] || null; },
    diagnosisFor(stage) {
      const v = this.verdicts[stage];
      const d = v && v.evidence && v.evidence.diagnosis ? v.evidence.diagnosis : (v && v.diagnosis);
      return d && String(d).trim() ? d : null;
    },
    lightColor(stage) {
      if (this.running[stage]) return '#94A3B8';
      const v = this.verdicts[stage];
      return v ? (VERDICT_COLORS[v.verdict] || '#94A3B8') : '#D1D5DB';
    },
    invColor(status) {
      return { PASS: '#10B981', FAIL: '#EF4444', WARN: '#F59E0B', SKIP: '#9CA3AF' }[status] || '#9CA3AF';
    },
    fingerprintStale(stage) {
      const v = this.verdicts[stage];
      return v && v.contract_fingerprint && this.contractFingerprint
        && v.contract_fingerprint !== this.contractFingerprint;
    },
    ago(ts) {
      const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
      if (mins < 60) return `${mins}m ago`;
      if (mins < 60 * 48) return `${Math.round(mins / 60)}h ago`;
      return `${Math.round(mins / 1440)}d ago`;
    },

    async load() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get(this.auditUrl);
        this.verdicts = data.verdicts || {};
        this.jobs = data.jobs || {};
        // Resume polling any recent in-flight job per stage.
        for (const [stage, job] of Object.entries(this.jobs)) {
          const ageMin = (Date.now() - new Date(job.created_at).getTime()) / 60000;
          if (['QUEUED', 'PROCESSING'].includes(job.status) && ageMin < 5) {
            this.running = { ...this.running, [stage]: true };
            this.pollCounts[stage] = 0;
            this.schedulePoll(stage, job.id);
          }
        }
      } catch (e) {
        this.error = `Could not load audit: ${e?.response?.data?.error || e.message}`;
      } finally {
        this.loading = false;
      }
    },

    async run(stage) {
      this.running = { ...this.running, [stage]: true };
      this.runError = { ...this.runError, [stage]: null };
      try {
        const { data } = await axios.post(this.runUrl, { stage });
        if (data.status === 'FAILED') throw new Error('The audit job failed to start.');
        this.pollCounts[stage] = 0;
        this.schedulePoll(stage, data.job_id);
      } catch (e) {
        this.running = { ...this.running, [stage]: false };
        this.runError = { ...this.runError, [stage]: `Failed: ${e?.response?.data?.error || e.message}` };
      }
    },

    schedulePoll(stage, jobId) {
      this._timers = this._timers || {};
      clearTimeout(this._timers[stage]);
      this._timers[stage] = setTimeout(() => this.poll(stage, jobId), POLL_MS);
    },
    async poll(stage, jobId) {
      this.pollCounts[stage] = (this.pollCounts[stage] || 0) + 1;
      try {
        const job = await getJob(jobId);
        if (job.status === 'COMPLETED') {
          this.running = { ...this.running, [stage]: false };
          await this.load();
          return;
        }
        if (job.status === 'FAILED') {
          this.running = { ...this.running, [stage]: false };
          this.runError = {
            ...this.runError,
            [stage]: `Check failed: ${job.error_message || 'the worker did not complete.'}`,
          };
          return;
        }
      } catch (e) { /* transient — keep polling */ }
      if (this.pollCounts[stage] >= MAX_POLLS) {
        this.running = { ...this.running, [stage]: false };
        this.runError = { ...this.runError, [stage]: 'Timed out — check back later.' };
        return;
      }
      this.schedulePoll(stage, jobId);
    },
  },
};
</script>
